import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { kycAPI } from '../../services/api';

export interface KYCDocument {
  id: string;
  type: 'passport' | 'national_id' | 'drivers_license' | 'utility_bill' | 'bank_statement';
  documentNumber: string;
  expiryDate?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  uploadedAt: string;
  updatedAt: string;
}

export interface KYCStatus {
  status: 'not_started' | 'in_progress' | 'verified' | 'rejected';
  verificationDate?: string;
  rejectionReason?: string;
}

export interface KYCState {
  documents: KYCDocument[];
  status: KYCStatus | null;
  isLoading: boolean;
  error: string | null;
  uploading: boolean;
  deleting: boolean;
}

const initialState: KYCState = {
  documents: [],
  status: null,
  isLoading: false,
  error: null,
  uploading: false,
  deleting: false,
};

// Async thunks
export const fetchKYCStatus = createAsyncThunk(
  'kyc/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await kycAPI.getStatus();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch KYC status');
    }
  }
);

export const fetchKYCDocuments = createAsyncThunk(
  'kyc/fetchDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await kycAPI.getDocuments();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch KYC documents');
    }
  }
);

export const uploadKYCDocument = createAsyncThunk(
  'kyc/uploadDocument',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await kycAPI.uploadDocument(formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to upload document');
    }
  }
);

export const deleteKYCDocument = createAsyncThunk(
  'kyc/deleteDocument',
  async (documentId: string, { rejectWithValue }) => {
    try {
      await kycAPI.deleteDocument(documentId);
      return { documentId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete document');
    }
  }
);

const kycSlice = createSlice({
  name: 'kyc',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch KYC Status
      .addCase(fetchKYCStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKYCStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload.status;
        state.error = null;
      })
      .addCase(fetchKYCStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch KYC Documents
      .addCase(fetchKYCDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKYCDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documents = action.payload.documents || [];
        state.error = null;
      })
      .addCase(fetchKYCDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Upload KYC Document
      .addCase(uploadKYCDocument.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadKYCDocument.fulfilled, (state, action) => {
        state.uploading = false;
        state.documents.unshift(action.payload.document);
        state.error = null;
      })
      .addCase(uploadKYCDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })
      // Delete KYC Document
      .addCase(deleteKYCDocument.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteKYCDocument.fulfilled, (state, action) => {
        state.deleting = false;
        state.documents = state.documents.filter(doc => doc.id !== action.payload.documentId);
        state.error = null;
      })
      .addCase(deleteKYCDocument.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = kycSlice.actions;
export default kycSlice.reducer;
